import { PropTypes } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import AgentActions from "~/actions/agent_actions";
import OrganizationTeamsSelect from "~/components/forms/dropdowns/OrganizationTeamsSelect";
import AppDispatcher from "~/dispatchers/app_dispatcher";
import AdditionalDetailsSection from "~/features/customFields/AdditionalDetailsSection";
import { customFieldsRequest } from "~/features/customFields/api";
import LeadDrawerActionTypes from "~/types/lead_drawer_action_types";
import AppModal from "./app_modal";

const ReassignLeadModal = ({ teamID, leadID, onCleanupComplete }) => {
  const [newTeam, setNewTeam] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [cleanup, setCleanup] = useState(null);
  const [notice, setNotice] = useState(null);
  const [reviewRequired, setReviewRequired] = useState(false);
  const modal = useRef(null);
  const inFlight = useRef(false);
  const active = useRef(true);
  const sequence = useRef(0);
  const previousIdentity = useRef(`${teamID}:${leadID}`);
  const identity = `${teamID}:${leadID}`;
  if (previousIdentity.current !== identity) {
    previousIdentity.current = identity;
    sequence.current += 1;
  }
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      sequence.current += 1;
    };
  }, []);
  useEffect(() => {
    setFailure(null);
    setCleanup(null);
    setNotice(null);
    setNewTeam(null);
    setReviewRequired(false);
    setBusy(false);
    inFlight.current = false;
  }, [teamID, leadID]);
  const submit = async () => {
    if (inFlight.current || reviewRequired || cleanup) {
      return;
    }
    if (!newTeam) {
      setFailure({ message: "Select an account." });
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setFailure(null);
    setNotice(null);
    const dispatched = sequence.current;
    try {
      const result = await customFieldsRequest({
        teamId: teamID,
        path: `/leads/${leadID}/reassign_lead`,
        method: "POST",
        body: { lead_id: leadID, team_id: newTeam },
      });
      if (!active.current || dispatched !== sequence.current) {
        return;
      }
      GlobalContainer.notify(
        result?.message || "Contact reassigned.",
        "success",
      );
      AppDispatcher.dispatch({
        type: LeadDrawerActionTypes.REASSIGN_LEAD_DONE,
        activity: { message: result?.message || "Contact reassigned." },
      });
      AgentActions.reloadLeadTables();
      $(modal.current.modal).modal("hide");
    } catch (error) {
      if (!active.current || dispatched !== sequence.current) {
        return;
      }
      setFailure(
        error.uncertain
          ? {
              message:
                "We couldn't confirm whether the contact was reassigned. Reload and review before trying again.",
            }
          : {
              message: "The contact could not be reassigned.",
              ...error.details,
              code: error.code,
            },
      );
      setReviewRequired(
        error.uncertain || error.code === "custom_fields_transfer_blocked",
      );
    } finally {
      if (active.current && dispatched === sequence.current) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  };
  const reload = async () => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    setBusy(true);
    const dispatched = sequence.current;
    try {
      await customFieldsRequest({ teamId: teamID, path: `/leads/${leadID}` });
      if (!active.current || dispatched !== sequence.current) {
        return;
      }
      setFailure(null);
      setCleanup(null);
      setReviewRequired(false);
      setNotice(
        "Contact reloaded. Review the destination before selecting Reassign again.",
      );
    } catch {
      if (active.current && dispatched === sequence.current) {
        setFailure({
          message:
            "This contact could not be reloaded in the current team. Close this dialog and check your contacts.",
        });
      }
    } finally {
      if (active.current && dispatched === sequence.current) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  };
  const cleanupDone = () => {
    setCleanup(null);
    setFailure(null);
    setReviewRequired(false);
    setNotice(
      "Additional Details cleared. Select Reassign when you are ready to move the contact.",
    );
    onCleanupComplete?.(teamID, leadID);
  };
  return (
    <AppModal
      containerID="secondary-modal"
      modalClass="modal modal-overlay"
      dialogClass="modal-dialog modal-lg"
      ref={modal}
    >
      <div className="modal-header">
        <h5 className="modal-title" id="appModalLabel">
          Reassign
        </h5>
        <button
          type="button"
          className="close"
          data-dismiss="modal"
          aria-label="Close"
          disabled={busy}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div className="modal-body">
        <div className="form-group mr5">
          <label htmlFor="organization-team-ids">Organization Accounts</label>
          <OrganizationTeamsSelect
            id="organization-team-ids"
            placeholder="Select an Account..."
            value={newTeam}
            isDisabled={busy || !!cleanup || reviewRequired}
            isClearable
            onChange={(item) => {
              setNewTeam(item?.value ?? null);
              setFailure(null);
            }}
          />
        </div>
        {notice && <p role="status">{notice}</p>}
        {failure && (
          <div role="alert" className="alert alert-warning">
            <p>
              {typeof failure.message === "string"
                ? failure.message
                : "The contact could not be reassigned."}
            </p>
            {failure.code === "custom_fields_transfer_blocked" &&
              failure.cleanup_context && (
                <button
                  type="button"
                  className="btn btn-link text-blue shadow-none border-0"
                  onClick={() => {
                    setCleanup(failure.cleanup_context);
                    setFailure(null);
                  }}
                >
                  Open Additional Details
                </button>
              )}
            {failure.code === "custom_fields_transfer_blocked" &&
              failure.variant === "upgrade_cta" && (
                <a href="/billing" className="btn btn-primary text-white">
                  View upgrade options
                </a>
              )}
          </div>
        )}
        {cleanup && (
          <>
            <AdditionalDetailsSection
              key={`${identity}:${cleanup}`}
              teamId={teamID}
              leadId={leadID}
              isAdmin
              cleanupContext={cleanup}
              onCleanupComplete={cleanupDone}
              onBusyChange={(value) => {
                inFlight.current = value;
                setBusy(value);
              }}
            />
            <button
              type="button"
              className="btn btn-link text-blue shadow-none border-0"
              disabled={busy}
              onClick={() => {
                setCleanup(null);
                setReviewRequired(true);
              }}
            >
              Return to Reassign
            </button>
          </>
        )}
        {reviewRequired && !cleanup && !failure?.cleanup_context && (
          <button
            type="button"
            className="btn btn-secondary mt-3"
            onClick={reload}
            disabled={busy}
          >
            Reload and Review
          </button>
        )}
      </div>
      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-secondary"
          data-dismiss="modal"
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || !!cleanup || reviewRequired}
          onClick={submit}
        >
          {busy ? "Processing…" : "Reassign"}
        </button>
      </div>
    </AppModal>
  );
};
ReassignLeadModal.propTypes = {
  teamID: PropTypes.number.isRequired,
  leadID: PropTypes.number.isRequired,
  organizationID: PropTypes.number.isRequired,
  onCleanupComplete: PropTypes.func,
};
export default ReassignLeadModal;
